// Compose-path counterpart to slackMarkdown.ts (the render path). The Composer's
// model value is the Slack WIRE string - plain text plus the interactive control
// tokens the backend stores and delivers: user `<@ID|name>` and channel
// `<#ID|name>`. These helpers convert between that wire string and the Tiptap
// document so chips round-trip: parse tokens -> chip nodes on load, serialize
// chip nodes -> tokens on every edit.
//
// We emit the `|name` fallback (the same shape slackMarkdown READS) so a draft
// reloaded without a live participant directory still shows a readable label.
// Text nodes carry their formatting as Slack mrkdwn (bold -> *x*, italic -> _x_,
// code -> `x`, strike -> ~x~) so typed/pasted markdown round-trips: the chat
// renders it back via slackToMarkdown. (docFromWire stays plain text - the
// editor's own input/paste rules re-apply marks on edit; only a restored draft
// would show literal mrkdwn, an accepted edge.) Block nesting is handled
// defensively so a stray list/quote can't drop its text.
import type { JSONContent } from '@tiptap/vue-3';

/** A selectable `@`-mention (user/agent). `id` is what ships in `<@id>`. */
export interface MentionItem { id: string; label: string; avatarUrl?: string }
/** A selectable `#`-channel reference. `id` is what ships in `<#id>`. */
export interface ChannelItem { id: string; label: string }
/** A `/`-command the host app handles on select (no node is inserted). */
export interface SlashCommand { id: string; label: string; hint?: string }

/** The Tiptap node names the two chip extensions register (see Composer.vue). */
export const MENTION_NODE = 'mention';
export const CHANNEL_NODE = 'channelMention';

// Permissive id class: we control both ends, so round-trip whatever id the app
// supplies (Slack ids are `[A-Z0-9]+`, but app user ids may differ). Stops at a
// pipe (name separator) or the closing bracket.
const TOKEN_RE = /<([@#])([^|>]+)(?:\|([^>]*))?>/g;

function inlineFromText(line: string): JSONContent[] {
  const out: JSONContent[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  TOKEN_RE.lastIndex = 0;
  while ((m = TOKEN_RE.exec(line))) {
    if (m.index > last) out.push({ type: 'text', text: line.slice(last, m.index) });
    const sigil = m[1];
    const id = m[2] ?? '';
    const label = m[3] && m[3].length ? m[3] : id;
    out.push({ type: sigil === '@' ? MENTION_NODE : CHANNEL_NODE, attrs: { id, label } });
    last = m.index + m[0].length;
  }
  if (last < line.length) out.push({ type: 'text', text: line.slice(last) });
  return out;
}

/** Wire string -> Tiptap doc JSON. Each `\n` becomes its own paragraph; tokens
 *  within a line become chip nodes. Empty input yields one empty paragraph so
 *  the editor has a valid cursor target. */
export function docFromWire(wire: string | null | undefined): JSONContent {
  const lines = (wire ?? '').split('\n');
  const content: JSONContent[] = lines.map((line) => {
    const inline = inlineFromText(line);
    return inline.length ? { type: 'paragraph', content: inline } : { type: 'paragraph' };
  });
  return { type: 'doc', content: content.length ? content : [{ type: 'paragraph' }] };
}

// Wrap a text run in Slack mrkdwn for its marks. Code is literal in mrkdwn, so a
// code run is never also bold/italic-wrapped. Tiptap merges adjacent same-mark
// runs into one text node, so this stays one clean pair per span.
function applyMarks(text: string, marks?: { type: string }[]): string {
  if (!text || !marks?.length) return text;
  const has = (t: string) => marks.some((m) => m.type === t);
  if (has('code')) return `\`${text}\``;
  let s = text;
  if (has('strike')) s = `~${s}~`;
  if (has('italic')) s = `_${s}_`;
  if (has('bold')) s = `*${s}*`;
  return s;
}

function inlineToWire(nodes: JSONContent[]): string {
  let out = '';
  for (const n of nodes) {
    if (n.type === 'text') out += applyMarks(n.text ?? '', n.marks as { type: string }[] | undefined);
    else if (n.type === 'hardBreak') out += '\n';
    else if (n.type === MENTION_NODE || n.type === CHANNEL_NODE) {
      const sigil = n.type === MENTION_NODE ? '@' : '#';
      const id = (n.attrs?.id as string) ?? '';
      const label = (n.attrs?.label as string) ?? '';
      out += label && label !== id ? `<${sigil}${id}|${label}>` : `<${sigil}${id}>`;
    }
  }
  return out;
}

function blockToWire(node: JSONContent): string {
  const content = node.content ?? [];
  if (!content.length) return '';
  // Inline-bearing block (paragraph/heading): serialize its inline run. A block
  // whose children are themselves blocks (list, blockquote) is flattened one
  // level per line so nothing silently drops.
  const inline = content.some(
    (c) => c.type === 'text' || c.type === 'hardBreak' || c.type === MENTION_NODE || c.type === CHANNEL_NODE,
  );
  return inline ? inlineToWire(content) : content.map(blockToWire).join('\n');
}

/** Tiptap doc JSON -> wire string (paragraphs joined by `\n`, chips -> tokens). */
export function wireFromDoc(doc: JSONContent | null | undefined): string {
  if (!doc?.content) return '';
  return doc.content.map(blockToWire).join('\n');
}
